import {Component, inject, OnInit} from '@angular/core';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Router} from '@angular/router';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {PatientFormFactory} from '../../factories/patient-form.factory';
import {MatIconModule} from '@angular/material/icon';
import {MatSelectModule} from '@angular/material/select';
import {Sex} from '../../../../core/enums/sex.enum';
import {Language} from '../../../../core/enums/language.enum';
import {CountryGroup, CountryService} from '../../../../core/services/country.service';
import {TextButton} from '../../../../shared/components/text-button/text-button.component';
import {DateMaskDirective} from '../../../../shared/directives/date-mask.directive';
import {PhoneInput} from '../../../../shared/components/phone-input/phone-input.component';
import {RegisterDto} from '../../dtos/register.dto';
import {PatientManagementService} from '../../patient-management.service';
import {IconButtonComponent} from '../../../../shared/components/icon-button/icon-button.component';
import {PatientMapper} from '../../mappers/patient.mapper';
import {MatDialog} from '@angular/material/dialog';
import {VerifyDialog} from '../../../authentication/dialogs/verify/verify.dialog';
import {Location} from '@angular/common';
import {RedirectService} from '../../../../core/services/redirect.service';
import {HttpErrorResponse} from '@angular/common/http';
import {ErrorService} from '../../../../core/services/error.service';
import {WindowReferenceService} from '../../../../core/services/window.service';
import {MemoryService} from '../../../../core/services/memory.service';

@Component({
  selector: 'app-register',
  imports: [
    TranslocoPipe,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatIconModule,
    MatSelectModule,
    TextButton,
    IconButtonComponent,
    DateMaskDirective,
    PhoneInput,
  ],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss'
})
export class RegisterComponent implements OnInit {
  router: Router = inject(Router);
  fb: FormBuilder = inject(FormBuilder);
  patientManagementService: PatientManagementService = inject(PatientManagementService);
  dialog: MatDialog = inject(MatDialog);
  location: Location = inject(Location);
  redirectService = inject(RedirectService);
  translocoService = inject(TranslocoService);
  countryService = inject(CountryService);
  errorService = inject(ErrorService);
  memoryService: MemoryService = inject(MemoryService);
  windowSerivce = inject(WindowReferenceService);

  error?: string;
  hidePassword: boolean = true;
  hidePasswordRepeat: boolean = true;
  registerForm!: FormGroup;

  get sexes(): Sex[] {
    return Object.values(Sex)
  }

  get languages(): Language[] {
    return Object.values(Language)
  }

  get countryGroups(): CountryGroup[] {
    return this.countryService.countryGroups;
  }

  get isPasswordValid() {
    return this.registerForm.get('password')?.valid || !this.registerForm.get('password')?.touched;
  }

  get isPasswordRepeatValid() {
    return this.registerForm.get('passwordRepeat')?.valid || !this.registerForm.get('passwordRepeat')?.touched;
  }

  get passwordMatch() {
    return this.registerForm.hasError("passwordMatch")
  }

  get phoneRequired() {
    return this.registerForm.hasError("phoneRequired") && (this.registerForm.get('phone')?.touched || this.registerForm.get('mobile')?.touched);
  }

  ngOnInit() {
    this.registerForm = PatientFormFactory.createRegisterForm(this.fb);
  }

  cancel() {
    this.location.back();
  }

  register() {
    // We help the user with the phone number. So in case the leading country code is missing. We add it automatically. Same counts for the Country code.
    if (this.registerForm.value.countryCode == '') {
      this.registerForm.value.countryCode = 'CH';
    }

    if (this.registerForm.value.phone != '' && this.registerForm.value.phone.length > 9) {
      this.registerForm.value.phone = this.ensureCountryCode(this.registerForm.value.phone, this.registerForm.value.countryCode);
    }

    if (this.registerForm.value.mobile != '' && this.registerForm.value.mobile.length > 9) {
      this.registerForm.value.mobile = this.ensureCountryCode(this.registerForm.value.mobile, this.registerForm.value.countryCode);
    }

    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid) {
      return;
    }

    let registerDto: RegisterDto = PatientMapper.toRegisterDto(this.registerForm.value, this.memoryService.customerId, this.memoryService.locationId);
    this.patientManagementService.registerPatient(registerDto).subscribe({
      next: () => {
        this.error = undefined;
        if (this.windowSerivce.pushEvents) {
          var payload = {event: 'registration_completed', location: this.memoryService.locationId ?? 0};
          this.windowSerivce.push(payload);
        }

        this.dialog.open(VerifyDialog, {autoFocus: false}).afterClosed().subscribe((value: boolean) => {
          if (!value) {
            return;
          }

          const redirectPayload = this.redirectService.consume();
          if (!redirectPayload) {
            return;
          }

          this.router.navigateByUrl(redirectPayload?.url, {state: redirectPayload.state});
        });
      },
      error: (error: HttpErrorResponse) => {
        if (error.status == 409) {
          this.error = this.translocoService.translate('registration.error.conflict');
          return;
        }

        this.error = undefined;
        this.errorService.handleError(error, this.translocoService.translate('error.title.register'));
      }
    });
  }

  ensureCountryCode(phone: string, defaultCountry: string = "CH"): string {
    if (!phone) return phone;

    // Remove spaces, dashes, parentheses etc.
    let cleaned = phone.replace(/[^\d+]/g, "");
    // Case 1: Already in + format
    if (cleaned.startsWith("+")) {
      return cleaned;
    }

    // Case 2: Starts with 00 → convert to +
    if (cleaned.startsWith("00")) {
      return "+" + cleaned.slice(2);
    }

    // Case 3: Local number starting with 0 → remove leading 0
    if (cleaned.startsWith("0")) {
      cleaned = cleaned.slice(1);
    }

    let defaultCountryCode = "+41";
    switch (defaultCountry) {
      case "DE":
        defaultCountryCode = "+49";
        break;
      case "IT":
        defaultCountryCode = "+39";
        break;
      case "FR":
        defaultCountryCode = "+33";
        break;
      case "AT":
        defaultCountryCode = "+43";
        break;
      case "LI":
        defaultCountryCode = "+423";
        break;
    }

    // Case 4: No country code → prepend default
    return defaultCountryCode + cleaned;
  }

  togglePasswordVisibility() {
    this.hidePassword = !this.hidePassword;
  }

  togglePasswordRepeatVisibility() {
    this.hidePasswordRepeat = !this.hidePasswordRepeat;
  }
}
